import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from './api';

const PLATFORM_FEE_RATE = 0.1;
const PAYOUT_SPLITS = [
  { rank: 1, pct: 40 },
  { rank: 2, pct: 20 },
  { rank: 3, pct: 12 },
  { rank: 4, pct: 10 },
  { rank: 5, pct: 6 },
  { rank: 6, pct: 3 },
  { rank: 7, pct: 3 },
  { rank: 8, pct: 3 },
  { rank: 9, pct: 3 },
];

function roundToTwo(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function formatOrdinal(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return String(value || '');
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const mod10 = n % 10;
  if (mod10 === 1) return `${n}st`;
  if (mod10 === 2) return `${n}nd`;
  if (mod10 === 3) return `${n}rd`;
  return `${n}th`;
}

function payoutTone(rank) {
  switch (Number(rank)) {
    case 1:
      return {
        borderColor: 'rgba(255,138,214,0.55)',
        background: 'linear-gradient(135deg, rgba(255,138,214,0.38), rgba(0,0,0,0.14))',
        boxShadow: '0 16px 40px rgba(255,138,214,0.12), 0 12px 26px rgba(0,0,0,0.24)',
        backdropFilter: 'none',
      };
    case 2:
      return {
        borderColor: 'rgba(160,205,255,0.45)',
        background: 'linear-gradient(135deg, rgba(120,170,255,0.30), rgba(0,0,0,0.14))',
        boxShadow: '0 16px 40px rgba(120,170,255,0.10), 0 12px 26px rgba(0,0,0,0.24)',
        backdropFilter: 'none',
      };
    case 3:
      return {
        borderColor: 'rgba(214,166,102,0.46)',
        background: 'linear-gradient(135deg, rgba(214,166,102,0.26), rgba(0,0,0,0.14))',
        boxShadow: '0 16px 40px rgba(214,166,102,0.10), 0 12px 26px rgba(0,0,0,0.24)',
        backdropFilter: 'none',
      };
    default:
      return {};
  }
}

function formatCoins(value) {
  return Number(value || 0).toLocaleString();
}

function getEntryCostBreakdown(tournament) {
  const entryFeeRaw = tournament?.entryFee;
  const totalRaw = tournament?.totalEntryCost;

  const entryFee = Number(entryFeeRaw);
  if (Number.isFinite(entryFee)) {
    const base = roundToTwo(entryFee);
    const platformFee = roundToTwo(base * PLATFORM_FEE_RATE);
    const total = roundToTwo(base + platformFee);
    return { base, platformFee, total };
  }

  const total = Number(totalRaw);
  if (Number.isFinite(total)) {
    const base = roundToTwo(total / (1 + PLATFORM_FEE_RATE));
    const platformFee = roundToTwo(total - base);
    return { base, platformFee, total: roundToTwo(total) };
  }

  return { base: 0, platformFee: 0, total: 0 };
}

function formatEntryCostBreakdown(tournament) {
  const { base, platformFee } = getEntryCostBreakdown(tournament);
  return `${formatCoins(base)} + ${formatCoins(platformFee)}`;
}

function getDisplayedPrizePool(tournament) {
  const { base } = getEntryCostBreakdown(tournament);
  const maxPlayers = Number(tournament?.maxPlayers || 0);
  return roundToTwo(base * Math.max(0, maxPlayers));
}

function formatDateTime(value) {
  if (!value) return 'Pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Pending';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return styles.badgeSuccess;
  if (s === 'completed') return styles.badgeMuted;
  if (s === 'upcoming' || s === 'open') return styles.badgeWarning;
  return styles.badgeDefault;
}

function getPrimaryAction({
  selectedTournament,
  isRegistered,
  canOpenTournament,
  canJoin,
  joining,
  isFull,
}) {
  const status = String(selectedTournament?.status || '').toLowerCase();
  if (status === 'completed' && isRegistered) return 'Finished';
  if (canOpenTournament) return 'Enter Tournament';
  if (isRegistered) return status === 'active' ? 'Enter Tournament' : 'Joined';
  if (status === 'active') return 'Already Started';
  if (isFull) return 'Ended';
  if (joining) return 'Joining...';
  if (canJoin) return 'Join Tournament';
  return 'Unavailable';
}

function getChampion(tournament) {
  return (tournament?.rankings || []).find((entry) => Number(entry.finalRank || 0) === 1) || null;
}

function getTopRanks(tournament, limit = 3) {
  return (tournament?.rankings || []).slice(0, limit);
}

function hasTournamentGamesAccess(tournament) {
  const status = String(tournament?.status || '').toLowerCase();
  return ['active', 'completed'].includes(status);
}

function getMatchDisplayTime(match) {
  return formatDateTime(match?.completedAt || match?.createdAt || null);
}

function getRoundSortValue(round) {
  return (
    Number(round?.stageOrder || 0) * 1000
    + Number(round?.roundNumber || 0)
  );
}

function getTournamentGameRounds(tournament) {
  return (tournament?.rounds || [])
    .filter((round) => String(round?.bracketType || '') === 'main')
    .filter((round) => Array.isArray(round?.matches) && round.matches.length > 0)
    .slice()
    .sort((a, b) => getRoundSortValue(b) - getRoundSortValue(a));
}

function getRelatedSubRounds(mainRound, allRounds) {
  const mainRoundNumber = Number(mainRound?.roundNumber || 0);
  return [...(allRounds || [])]
    .filter((round) => String(round?.bracketType || '') !== 'main')
    .filter((round) => {
      const sourceMainRoundNumber = Number(round?.sourceMainRoundNumber || 0);
      if (sourceMainRoundNumber > 0) {
        return sourceMainRoundNumber === mainRoundNumber;
      }
      if (mainRoundNumber === 3) return String(round?.bracketType || '') === 'placement-8-9';
      if (mainRoundNumber === 4) return String(round?.bracketType || '') === 'placement-5-7';
      if (mainRoundNumber === 5) return String(round?.bracketType || '') === 'placement-3-4';
      return false;
    })
    .filter((round) => Array.isArray(round?.matches) && round.matches.length > 0)
    .sort((a, b) => {
      if (Number(a?.roundNumber || 0) !== Number(b?.roundNumber || 0)) {
        return Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0);
      }
      return Number(a?.stageOrder || 0) - Number(b?.stageOrder || 0);
    });
}

function getRoundCardTone(index) {
  const tones = [
    {
      background: 'linear-gradient(135deg, rgba(126,74,35,0.44), rgba(48,27,17,0.94))',
      borderColor: 'rgba(255,198,120,0.28)',
      boxShadow: '0 14px 28px rgba(0,0,0,0.2)',
    },
    {
      background: 'linear-gradient(135deg, rgba(66,86,132,0.34), rgba(24,22,39,0.94))',
      borderColor: 'rgba(142,181,255,0.24)',
      boxShadow: '0 14px 28px rgba(0,0,0,0.22)',
    },
    {
      background: 'linear-gradient(135deg, rgba(60,112,88,0.34), rgba(16,34,28,0.94))',
      borderColor: 'rgba(129,220,182,0.22)',
      boxShadow: '0 14px 28px rgba(0,0,0,0.2)',
    },
  ];
  return tones[index % tones.length];
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={styles.sectionHeader}>
      <div style={styles.sectionTitle}>{title}</div>
      {subtitle ? <div style={styles.sectionSubtitle}>{subtitle}</div> : null}
    </div>
  );
}

export default function TournamentLobby({ user, onWalletChange, onOpenTournamentLive, onBack }) {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showGamesModal, setShowGamesModal] = useState(false);
  const [joinConfirm, setJoinConfirm] = useState(null);
  const [gamesTournament, setGamesTournament] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  const detailRequestIdRef = useRef(0);
  const featuredTournamentRef = useRef(null);
  const toastTimerRef = useRef(null);

  function scrollToFeaturedTournament() {
    if (typeof window === 'undefined') return;
    if (!featuredTournamentRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const rect = featuredTournamentRef.current.getBoundingClientRect();
    const top = Math.max(0, window.scrollY + rect.top - 12);
    window.scrollTo({ top, behavior: 'smooth' });
  }

  async function loadTournamentDetail(nextId, seedTournament = null, options = {}) {
    const { scrollToTop = false } = options;
    if (!nextId) {
      setSelectedTournament(null);
      setDetailLoading(false);
      return;
    }

    setShowLeaderboard(false);
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;

    setSelectedTournamentId(nextId);
    setDetailLoading(true);
    setError('');

    if (scrollToTop) {
      scrollToFeaturedTournament();
    }

    if (seedTournament) {
      setSelectedTournament({
        ...seedTournament,
        // Avoid showing stale detail fields from the previously selected tournament.
        rankings: [],
        participants: seedTournament.participants || [],
      });
    }

    try {
      const detailRes = await api.get(`/matches/tournaments/${nextId}`);
      if (detailRequestIdRef.current !== requestId) return;
      setSelectedTournament(detailRes.data?.data?.tournament || null);
    } catch (err) {
      if (detailRequestIdRef.current !== requestId) return;
      setError(err?.response?.data?.message || 'Failed to load tournament details');
      setSelectedTournament(null);
    } finally {
      if (detailRequestIdRef.current !== requestId) return;
      setDetailLoading(false);
    }
  }

  async function loadTournaments(preferredId = '') {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/matches/tournaments');
      const list = res.data?.data?.tournaments || [];
      setTournaments(list);

      const nextId = preferredId || selectedTournamentId || list[0]?.id || '';
      const seedTournament = list.find((item) => String(item.id) === String(nextId)) || null;
      await loadTournamentDetail(nextId, seedTournament);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load tournaments');
      setTournaments([]);
      setSelectedTournament(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTournaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const text = String(error || message || '').trim();
    if (!text) return undefined;

    setToast({ type: error ? 'error' : 'ok', text });

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, error ? 6500 : 3500);

    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [error, message]);

  function dismissToast() {
    setToast(null);
    setError('');
    setMessage('');
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }

  function requestJoin(targetTournamentId, tournamentSeed = null) {
    if (!targetTournamentId) return;
    const seeded = tournamentSeed
      || (selectedTournament && String(selectedTournament.id) === String(targetTournamentId) ? selectedTournament : null)
      || tournaments.find((item) => String(item.id) === String(targetTournamentId))
      || null;

    setJoinConfirm({
      tournamentId: targetTournamentId,
      tournament: seeded,
    });
  }

  async function confirmJoin() {
    const targetTournamentId = joinConfirm?.tournamentId;
    setJoinConfirm(null);
    await handleJoin(targetTournamentId);
  }

  async function handleJoin(targetTournamentId = selectedTournamentId) {
    if (!targetTournamentId) return;
    setJoining(true);
    setMessage('');
    setError('');

    try {
      const res = await api.post(`/matches/tournaments/${targetTournamentId}/join`);
      await loadTournaments(targetTournamentId);
      if (typeof onWalletChange === 'function') onWalletChange();
      setMessage(res.data?.message || 'Tournament joined successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to join tournament');
    } finally {
      setJoining(false);
    }
  }

  async function handleOpenGames(targetTournamentId = selectedTournamentId, seedTournament = null) {
    if (!targetTournamentId) return;
    setShowGamesModal(true);
    setGamesLoading(true);

    if (seedTournament) {
      setGamesTournament(seedTournament);
    }

    try {
      if (String(selectedTournamentId) === String(targetTournamentId) && selectedTournament?.rounds) {
        setGamesTournament(selectedTournament);
        return;
      }

      const detailRes = await api.get(`/matches/tournaments/${targetTournamentId}`);
      setGamesTournament(detailRes.data?.data?.tournament || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load tournament games');
      setGamesTournament(seedTournament || null);
    } finally {
      setGamesLoading(false);
    }
  }

  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth < 1100;
  const useShowcaseLayout = true;

  const visibleTournaments = useMemo(() => {
    const normalized = String(statusFilter || 'all').toLowerCase();
    if (normalized === 'all') return tournaments;
    return tournaments.filter((item) => {
      const status = String(item?.status || '').toLowerCase();
      if (normalized === 'upcoming') return ['upcoming', 'open', 'not_started'].includes(status);
      if (normalized === 'active') return status === 'active' || status === 'live';
      if (normalized === 'completed') return status === 'completed' || status === 'done';
      return true;
    });
  }, [statusFilter, tournaments]);

  const canJoin = useMemo(() => {
    if (!selectedTournament) return false;
    const isFull = Number(selectedTournament.participantCount || 0) >= Number(selectedTournament.maxPlayers || 0);
    return !joining
      && !selectedTournament.myEntry
      && !isFull
      && selectedTournament.status !== 'active'
      && selectedTournament.status !== 'completed';
  }, [joining, selectedTournament]);

  const isFull = useMemo(() => {
    if (!selectedTournament) return false;
    return Number(selectedTournament.participantCount || 0) >= Number(selectedTournament.maxPlayers || 0);
  }, [selectedTournament]);

  const isRegistered = useMemo(() => {
    if (!selectedTournament) return false;
    if (selectedTournament.myEntry) return true;
    return (selectedTournament.participants || []).some((participant) => String(participant.userId || '') === String(user?.id || ''));
  }, [selectedTournament, user?.id]);

  const canOpenTournament = useMemo(() => {
    if (!selectedTournament) return false;
    return isRegistered && ['active', 'completed'].includes(String(selectedTournament.status || '').toLowerCase());
  }, [isRegistered, selectedTournament]);

  const selectedChampion = useMemo(() => getChampion(selectedTournament), [selectedTournament]);
  const selectedTopRanks = useMemo(() => getTopRanks(selectedTournament, 9), [selectedTournament]);
  const selectedGameRounds = useMemo(() => getTournamentGameRounds(gamesTournament), [gamesTournament]);
  const allGameRounds = useMemo(() => (gamesTournament?.rounds || []), [gamesTournament]);
  const selectedProgressPercent = useMemo(() => {
    if (!selectedTournament) return 0;
    return Math.min(
      100,
      Math.round(((selectedTournament.participantCount || 0) / Math.max(1, selectedTournament.maxPlayers || 1)) * 100)
    );
  }, [selectedTournament]);
  const releaseDone = Boolean(selectedTournament?.prizesAwardedAt && selectedTournament?.platformSettledAt);
  const actionLabel = getPrimaryAction({
    selectedTournament,
    isRegistered,
    canOpenTournament,
    canJoin,
    joining,
    isFull,
  });
  const shouldShowJoinedWaitingNote = actionLabel === 'Joined'
    && Number(selectedTournament?.activeRoundNumber || 0) < 1;

  return (
      <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : {}), padding: isMobile ? 6 : 18 }}>
      {toast ? (
        <div style={{ ...styles.toastBar, ...(toast.type === 'error' ? styles.toastBarError : styles.toastBarOk) }}>
          <div style={styles.toastText}>{toast.text}</div>
          <button type="button" onClick={dismissToast} style={styles.toastCloseBtn} aria-label="Dismiss notification">×</button>
        </div>
      ) : null}

      <div style={{ ...styles.heroShell, ...(isMobile ? styles.heroShellMobile : {}), padding: isMobile ? 6 : isTablet ? 10 : 10 }}>
        <div style={styles.heroGlowOne} />
        <div style={styles.heroGlowTwo} />

        <div style={{ ...styles.heroTopRow, ...(isMobile ? styles.heroTopRowMobile : {}) }}>
          <div style={{ ...styles.heroMain, ...(isMobile ? styles.heroMainMobile : {}) }}>
            <div style={styles.heroActions}>
              <button
                type="button"
                onClick={() => onBack?.()}
                style={{ ...styles.backButton, ...(isMobile ? styles.backButtonMobile : {}) }}
              >
                Back
              </button>
            </div>
            <div style={styles.kicker}>Tournament Arena</div>
            <div style={{ ...styles.title, fontSize: isMobile ? 13 : isTablet ? 18 : 20, ...(isMobile ? styles.titleMobile : {}) }}>
              Join upcoming tournaments and compete for the prize pool.
            </div>
            {!isMobile && (
            <div style={styles.meta}>
              Review entry cost, live progress, winners, and completion details before you join or enter a tournament.
            </div>
            )}
          </div>

        </div>

        {!isMobile && <div style={styles.heroPills}>
          <div style={styles.heroPill}>Single Elimination</div>
          <div style={styles.heroPill}>Top 9 Paid</div>
          <div style={styles.heroPill}>First to 5 Wins</div>
        </div>}

        <div style={{ ...styles.payoutStrip, ...(isMobile ? styles.payoutStripMobile : {}) }}>
          <div>
            <div style={styles.payoutTitle}>Prize Pool Payouts</div>
            <div style={styles.payoutSubtitle}>Top 9 players get paid (percentage split).</div>
          </div>
          <div style={{ ...styles.payoutTopRow, ...(isMobile ? styles.payoutTopRowMobile : {}) }}>
            {PAYOUT_SPLITS.slice(0, 3).map((split) => (
              <div
                key={split.rank}
                style={{
                  ...styles.payoutCardLarge,
                  ...payoutTone(split.rank),
                }}
              >
                <div style={styles.payoutRow}>
                  <span style={styles.payoutRankLarge}>{formatOrdinal(split.rank)}</span>
                  <span style={styles.payoutLabelInline}>
                    {split.rank === 1 ? 'Winner' : split.rank === 2 ? 'Runner-up' : '3rd Place'}
                  </span>
                  <span style={styles.payoutPctLarge}>{split.pct}%</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...styles.payoutGridRest, ...(isMobile ? styles.payoutGridRestMobile : {}) }}>
            {PAYOUT_SPLITS.slice(3).map((split) => (
              <div key={split.rank} style={{ ...styles.payoutCardSmall, ...payoutTone(split.rank) }}>
                <div style={styles.payoutRow}>
                  <span style={styles.payoutRankSmall}>{formatOrdinal(split.rank)}</span>
                  <span style={styles.payoutPctSmall}>{split.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {joinConfirm ? (
        <div
          style={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          onClick={() => setJoinConfirm(null)}
        >
          <div
            style={{ ...styles.modalCard, ...(isMobile ? styles.modalCardMobile : {}), ...styles.confirmCard }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Confirm Join</div>
                <div style={styles.modalSubtitle}>
                  {joinConfirm.tournament?.name ? `Join "${joinConfirm.tournament.name}"?` : 'Do you want to join this tournament?'}
                </div>
              </div>
              <button type="button" onClick={() => setJoinConfirm(null)} style={styles.modalCloseBtn}>Close</button>
            </div>

            <div style={styles.confirmBody}>
              <div style={styles.confirmLine}>
                <span style={styles.confirmKey}>Entry</span>
                <span style={styles.confirmValue}>{formatEntryCostBreakdown(joinConfirm.tournament)} coins</span>
              </div>
              <div style={styles.confirmLine}>
                <span style={styles.confirmKey}>Prize Pool</span>
                <span style={styles.confirmValue}>{formatCoins(getDisplayedPrizePool(joinConfirm.tournament))} coins</span>
              </div>
              <div style={styles.confirmHint}>Platform fee (10%) included in entry total.</div>
            </div>

            <div style={styles.confirmFooter}>
              <button type="button" onClick={() => setJoinConfirm(null)} style={{ ...styles.secondaryBtn, ...styles.confirmCancelBtn }}>Cancel</button>
              <button type="button" onClick={confirmJoin} disabled={joining} style={{ ...styles.joinBtn, ...(joining ? styles.joinBtnDisabled : {}), ...styles.confirmJoinBtn }}>
                {joining ? 'Joining...' : 'Yes, Join'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {useShowcaseLayout ? (
        <div style={{ ...styles.showcaseLayout, ...(isMobile ? styles.showcaseLayoutMobile : {}) }}>
          {false && (
          <section ref={featuredTournamentRef} style={{ ...styles.showcaseFeatured, ...(isMobile ? styles.showcaseFeaturedMobile : {}) }}>
            {loading && <div style={styles.emptyCard}>Loading tournaments...</div>}
            {!loading && !selectedTournament && <div style={styles.emptyCard}>No tournaments are available right now.</div>}

            {!loading && selectedTournament && (
              <div style={{ ...styles.showcaseFeaturedCard, ...(isMobile ? styles.showcaseFeaturedCardMobile : {}) }}>
                {!isMobile && <div style={styles.trophyArt} aria-hidden="true" />}

                <div style={styles.showcaseFeaturedContent}>
                  <div style={styles.showcaseFeaturedTop}>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.showcaseFeaturedLabel}>Featured Tournament</div>
                      <div style={{ ...styles.showcaseFeaturedTitle, fontSize: isMobile ? 16 : isTablet ? 20 : 24 }}>
                        {selectedTournament.name}
                      </div>
                      {!isMobile && (
                        <div style={styles.showcaseFeaturedMeta}>
                          {selectedTournament.participantCount}/{selectedTournament.maxPlayers} Players Joined &nbsp;•&nbsp; {selectedTournament.matchTypeLabel}
                        </div>
                      )}
                    </div>
                    <div style={{ ...styles.statusBadgeLarge, ...getStatusTone(selectedTournament.status) }}>
                      {selectedTournament.status}
                    </div>
                  </div>

                  <div style={{ ...styles.showcaseFeaturedChips, ...(isMobile ? styles.showcaseFeaturedChipsMobile : {}) }}>
                    <div style={styles.featureChip}>{selectedTournament.participantCount}/{selectedTournament.maxPlayers} Joined</div>
                    <div style={styles.featureChip}>{selectedTournament.pairingMode}</div>
                    <div style={styles.featureChip}>{selectedTournament.matchTypeLabel}</div>
                  </div>

                  <div style={{ ...styles.showcaseFeaturedStats, ...(isMobile ? styles.showcaseFeaturedStatsMobile : {}) }}>
                    <div style={styles.featuredStat}>
                      <div style={styles.featuredStatLabel}>Prize Pool</div>
                      <div style={styles.featuredStatValue}>{formatCoins(selectedTournament.prizePool)}</div>
                    </div>
                    <div style={styles.featuredStat}>
                      <div style={styles.featuredStatLabel}>Total Entry</div>
                      <div style={styles.featuredStatValue}>{formatEntryCostBreakdown(selectedTournament)} coins</div>
                    </div>
                    <div style={styles.featuredStat}>
                      <div style={styles.featuredStatLabel}>Completed</div>
                      <div style={styles.featuredStatValue}>{formatDateTime(selectedTournament.completedAt)}</div>
                    </div>
                  </div>

                  <div style={styles.progressBlock}>
                    <div style={styles.progressHead}>
                      <span style={styles.progressTitle}>Registration</span>
                      <span style={styles.progressValue}>{selectedProgressPercent}%</span>
                    </div>
                    <div style={styles.progressTrackLarge}>
                      <div style={{ ...styles.progressFillLarge, width: `${selectedProgressPercent}%` }} />
                    </div>
                  </div>
                </div>

                <div style={{ ...styles.ctaCard, ...(isMobile ? styles.ctaCardMobile : {}) }}>
                  <div style={styles.ctaMini}>Tournament Entry</div>
                  <div style={styles.ctaPrice}>{formatEntryCostBreakdown(selectedTournament)}</div>
                  <div style={styles.ctaSub}>
                    {String(selectedTournament.status || '').toLowerCase() === 'completed'
                      ? 'This tournament is finished.'
                      : isFull && !isRegistered
                        ? 'Registration closed. Tournament is full.'
                        : 'Entry fee + platform fee included'}
                  </div>
                  <button
                    type="button"
                    onClick={canOpenTournament ? () => onOpenTournamentLive?.(selectedTournament.id) : () => handleJoin(selectedTournament.id)}
                    disabled={!canJoin && !canOpenTournament}
                    style={{
                      ...styles.joinBtn,
                      ...((!canJoin && !canOpenTournament) ? styles.joinBtnDisabled : {}),
                    }}
                  >
                    {actionLabel}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowLeaderboard(true)}
                    disabled={detailLoading || !selectedTournament}
                    style={{ ...styles.secondaryBtn, ...styles.leaderboardBtn, ...((detailLoading || !selectedTournament) ? styles.secondaryBtnDisabled : {}) }}
                  >
                    View Leaderboard
                  </button>
                  {hasTournamentGamesAccess(selectedTournament) ? (
                    <button
                      type="button"
                      onClick={() => handleOpenGames(selectedTournament.id, selectedTournament)}
                      disabled={gamesLoading || !selectedTournament}
                      style={{ ...styles.secondaryBtn, ...styles.gamesBtn, ...((gamesLoading || !selectedTournament) ? styles.secondaryBtnDisabled : {}) }}
                    >
                      Games
                    </button>
                  ) : null}
                  {selectedTournament?.myEntry?.finalRank ? (
                    <div style={styles.ctaInfo}>You finished at rank #{selectedTournament.myEntry.finalRank}.</div>
                  ) : null}
                  {shouldShowJoinedWaitingNote ? (
                    <div style={styles.ctaInfo}>You will enter tournament when TavlaPro starts round 1.</div>
                  ) : null}
                  {selectedChampion ? (
                    <div style={styles.ctaInfo}>Winner: {selectedChampion.name}</div>
                  ) : null}
                </div>
              </div>
            )}
          </section>
          )}

          <section ref={featuredTournamentRef} style={styles.showcaseBrowse}>
            <div style={{ ...styles.browseHeader, ...(isMobile ? styles.browseHeaderMobile : {}) }}>
              <SectionHeader title="Tournaments" subtitle="Tap a card to view details." />
              <div style={styles.filterTabs}>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'upcoming', label: 'Upcoming' },
                  { id: 'active', label: 'Active' },
                  { id: 'completed', label: 'Completed' },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setStatusFilter(filter.id)}
                    style={{ ...styles.filterTab, ...(String(statusFilter) === filter.id ? styles.filterTabActive : {}) }}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {loading && <div style={styles.emptyCard}>Loading tournaments...</div>}

            {!loading && visibleTournaments.length > 0 && (
              <div style={{ ...styles.cardGrid, ...(isMobile ? styles.cardGridMobile : {}) }}>
                {visibleTournaments
                  .map((item) => {
                    const status = String(item.status || '').toLowerCase();
                    const isFullLocal = Number(item.participantCount || 0) >= Number(item.maxPlayers || 0);
                    const isRegisteredLocal = Boolean(item?.myEntry);
                    const isSelectedLocal = String(item.id) === String(selectedTournamentId);
                    const canOpenLocal = isRegisteredLocal && ['active', 'completed'].includes(status);
                    const canJoinLocal = !joining && !isRegisteredLocal && !isFullLocal && !['active', 'completed'].includes(status);
                    const canShowGamesLocal = hasTournamentGamesAccess(item);
                    const action = getPrimaryAction({
                      selectedTournament: item,
                      isRegistered: isRegisteredLocal,
                      canOpenTournament: canOpenLocal,
                      canJoin: canJoinLocal,
                      joining: joining && String(selectedTournamentId) === String(item.id),
                      isFull: isFullLocal,
                    });
                    const fill = Math.min(100, Math.round(((item.participantCount || 0) / Math.max(1, item.maxPlayers || 1)) * 100));

                    return (
                      <div
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => loadTournamentDetail(item.id, item, { scrollToTop: true })}
                        onMouseUp={(e) => e.currentTarget.blur()}
                        onTouchEnd={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            loadTournamentDetail(item.id, item, { scrollToTop: true });
                          }
                        }}
                        style={{ ...styles.tournamentCard, ...(isMobile ? styles.tournamentCardMobile : {}), ...(isSelectedLocal ? styles.tournamentCardActive : {}) }}
                      >
                        <div style={styles.tournamentCardTop}>
                          <div style={{ minWidth: 0 }}>
                            <div style={styles.tournamentCardTitle}>{item.name}</div>
                            <div style={styles.tournamentCardMeta}>
                              {item.participantCount}/{item.maxPlayers} Players Joined
                            </div>
                          </div>
                          <div style={{ ...styles.statusBadge, ...getStatusTone(item.status) }}>{item.status}</div>
                        </div>

                        <div style={styles.progressTrack}>
                          <div style={{ ...styles.progressFill, width: `${fill}%` }} />
                        </div>

                      <div style={{ ...styles.tournamentCardBottom, ...(isMobile ? styles.tournamentCardBottomMobile : {}) }}>
                        <div style={styles.coinRow}>
                          <span style={styles.coinDot} aria-hidden="true" />
                          <div style={styles.coinStack}>
                            <span style={styles.coinValue}>Entry: {formatEntryCostBreakdown(item)} coins</span>
                            <span style={styles.coinSubValue}>Prize Pool: {formatCoins(getDisplayedPrizePool(item))} coins</span>
                          </div>
                        </div>
                        <div style={{ ...styles.cardActions, ...(isMobile ? styles.cardActionsMobile : {}) }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canOpenLocal) onOpenTournamentLive?.(item.id);
                              else if (canJoinLocal) {
                                setSelectedTournamentId(item.id);
                                requestJoin(item.id, item);
                              }
                            }}
                            disabled={!canJoinLocal && !canOpenLocal}
                            style={{ ...styles.cardCtaBtn, ...((!canJoinLocal && !canOpenLocal) ? styles.cardCtaBtnDisabled : {}) }}
                          >
                            {action}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadTournamentDetail(item.id, item, { scrollToTop: true });
                              setShowLeaderboard(true);
                            }}
                            disabled={detailLoading && String(selectedTournamentId) === String(item.id)}
                            style={{
                              ...styles.cardSecondaryBtn,
                              ...styles.cardLeaderboardBtn,
                              ...((detailLoading && String(selectedTournamentId) === String(item.id)) ? styles.cardSecondaryBtnDisabled : {}),
                            }}
                          >
                            Leaderboard
                          </button>
                          {canShowGamesLocal ? (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleOpenGames(item.id, item);
                              }}
                              disabled={gamesLoading}
                              style={{
                                ...styles.cardSecondaryBtn,
                                ...styles.cardGamesBtn,
                                ...(gamesLoading ? styles.cardSecondaryBtnDisabled : {}),
                              }}
                            >
                              Games
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && visibleTournaments.length === 0 && (
              <div style={styles.emptyCard}>No tournaments found for this filter.</div>
            )}
          </section>

          {showLeaderboard && (
            <div
              style={styles.modalOverlay}
              role="dialog"
              aria-modal="true"
              onClick={() => setShowLeaderboard(false)}
            >
              <div
                style={{ ...styles.modalCard, ...(isMobile ? styles.modalCardMobile : {}) }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={styles.modalHeader}>
                  <div>
                    <div style={styles.modalTitle}>Leaderboard</div>
                    <div style={styles.modalSubtitle}>{selectedTournament?.name || 'Tournament'}</div>
                  </div>
                  <button type="button" onClick={() => setShowLeaderboard(false)} style={styles.modalCloseBtn}>Close</button>
                </div>
              {detailLoading ? (
                <div style={styles.detailLoaderCard}>Loading leaderboard...</div>
              ) : selectedTopRanks.length > 0 ? (
                <div style={styles.rankBoard}>
                  {selectedTopRanks.map((participant) => (
                    <div key={participant.id} style={{ ...styles.rankRow, ...(isMobile ? styles.rankRowMobile : {}) }}>
                      <div style={{ ...styles.rankCircle, ...(isMobile ? styles.rankCircleMobile : {}), ...(Number(participant.finalRank || 0) <= 3 ? styles.rankCircleTop : {}) }}>
                        #{participant.finalRank || '-'}
                      </div>
                      <div style={styles.rankContent}>
                        <div style={{ ...styles.rankName, ...(isMobile ? styles.rankNameMobile : {}) }}>
                          <span>{participant.name}</span>
                          {String(participant.userId || '') === String(user?.id || '') ? <span style={styles.youTag}>You</span> : null}
                        </div>
                        <div style={{ ...styles.rankMeta, ...(isMobile ? styles.rankMetaMobile : {}) }}>
                          @{participant.username} • {participant.finalRankLabel || participant.status}
                        </div>
                      </div>
                      <div style={{ ...styles.rankReward, ...(isMobile ? styles.rankRewardMobile : {}) }}>
                        {participant.prizeCoins ? `${formatCoins(participant.prizeCoins)} coins` : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyCardInner}>Rankings will appear once tournament results are assigned.</div>
              )}
              </div>
            </div>
          )}

        </div>
      ) : (
        <div style={{ ...styles.layout, gridTemplateColumns: isTablet ? '1fr' : '360px minmax(0, 1fr)' }}>
        <section style={{ ...styles.sidebar, padding: isMobile ? 14 : 18 }}>
          <SectionHeader
            title="Tournament List"
            subtitle="Completed events now show winner and finish details directly in the user list."
          />

          {loading && <div style={styles.emptyCard}>Loading tournaments...</div>}

          {!loading && tournaments.length === 0 && (
            <div style={styles.emptyCard}>No tournaments are available right now.</div>
          )}

          {!loading && tournaments.length > 0 && (
            <div style={{ ...styles.list, ...(isMobile ? styles.listMobile : {}), maxHeight: isTablet ? 'none' : 720 }}>
              {tournaments.map((item) => {
                const active = selectedTournamentId === item.id;
                const champion = getChampion(item);
                const myRank = Number(item?.myEntry?.finalRank || 0);
                const completed = String(item.status || '').toLowerCase() === 'completed';
                const fill = Math.min(
                  100,
                  Math.round(((item.participantCount || 0) / Math.max(1, item.maxPlayers || 1)) * 100)
                );

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => loadTournamentDetail(item.id, item, { scrollToTop: true })}
                    style={{
                      ...styles.listItem,
                      ...(isMobile ? styles.listItemMobile : {}),
                      ...(active ? styles.listItemActive : {}),
                    }}
                  >
                    <div style={styles.listItemTop}>
                      <div style={styles.listTitleBlock}>
                        <div style={{ ...styles.listTitle, ...(isMobile ? styles.listTitleMobile : {}) }}>{item.name}</div>
                        <div style={styles.listMeta}>
                          {item.participantCount}/{item.maxPlayers} players joined
                        </div>
                      </div>
                      <div style={{ ...styles.statusBadge, ...getStatusTone(item.status) }}>
                        {item.status}
                      </div>
                    </div>

                    <div style={styles.progressTrack}>
                      <div style={{ ...styles.progressFill, width: `${fill}%` }} />
                    </div>

                    <div style={{ ...styles.listFooter, ...(isMobile ? styles.listFooterMobile : {}) }}>
                      <span>Total Entry: {formatEntryCostBreakdown(item)}</span>
                      <span>Prize Pool: {formatCoins(getDisplayedPrizePool(item))}</span>
                      <span style={styles.modeBadge}>{item.pairingMode}</span>
                    </div>

                    {completed && (
                      <div style={styles.completedListMeta}>
                        <div style={styles.completedListLine}>
                          <span style={styles.completedListKey}>Winner</span>
                          <span style={styles.completedListValue}>{champion?.name || 'TBD'}</span>
                        </div>
                        <div style={styles.completedListLine}>
                          <span style={styles.completedListKey}>Finished</span>
                          <span style={styles.completedListValue}>{formatDateTime(item.completedAt)}</span>
                        </div>
                        {myRank > 0 && (
                          <div style={styles.completedListLine}>
                            <span style={styles.completedListKey}>Your Rank</span>
                            <span style={styles.completedListValue}>#{myRank}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section style={styles.mainPanel}>
          {!selectedTournament && <div style={styles.emptyCard}>Select a tournament to view details.</div>}

          {selectedTournament && (
            <>
              <div
                style={{
                  ...styles.topBanner,
                  ...(isMobile ? styles.topBannerMobile : {}),
                  gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : 'minmax(0, 1fr) 310px',
                  padding: isMobile ? 10 : 16,
                }}
              >
                <div style={styles.topBannerLeft}>
                  <div style={{ ...styles.topBannerHead, flexDirection: isMobile ? 'row' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...styles.detailTitle, fontSize: isMobile ? 18 : 26 }}>{selectedTournament.name}</div>
                      {!isMobile && <div style={styles.detailMeta}>
                        {selectedTournament.description || 'A tournament event with live participation tracking and completion details.'}
                      </div>}
                    </div>

                    <div style={{ ...styles.statusBadgeLarge, ...getStatusTone(selectedTournament.status) }}>
                      {selectedTournament.status}
                    </div>
                  </div>

                  <div style={{ ...styles.quickStats, ...(isMobile ? styles.quickStatsMobile : {}) }}>
                    <div style={styles.quickStat}>
                      <span style={styles.quickStatLabel}>Players</span>
                      <span style={styles.quickStatValue}>
                        {selectedTournament.participantCount}/{selectedTournament.maxPlayers}
                      </span>
                    </div>
                    <div style={styles.quickStat}>
                      <span style={styles.quickStatLabel}>Match Type</span>
                      <span style={styles.quickStatValue}>{selectedTournament.matchTypeLabel}</span>
                    </div>
                    <div style={styles.quickStat}>
                      <span style={styles.quickStatLabel}>Prize Pool</span>
                      <span style={styles.quickStatValue}>{formatCoins(selectedTournament.prizePool)}</span>
                    </div>
                    <div style={styles.quickStat}>
                      <span style={styles.quickStatLabel}>Completed</span>
                      <span style={styles.quickStatValue}>{formatDateTime(selectedTournament.completedAt)}</span>
                    </div>
                  </div>

                  <div style={styles.progressBlock}>
                    <div style={styles.progressHead}>
                      <span style={styles.progressTitle}>Tournament Progress</span>
                      <span style={styles.progressValue}>{selectedProgressPercent}% Full</span>
                    </div>
                    <div style={styles.progressTrackLarge}>
                      <div style={{ ...styles.progressFillLarge, width: `${selectedProgressPercent}%` }} />
                    </div>
                  </div>

                  {String(selectedTournament.status || '').toLowerCase() === 'completed' && (
                    <div style={styles.completedHero}>
                      <div style={styles.completedHeroItem}>
                        <span style={styles.completedHeroLabel}>Winner</span>
                        <span style={styles.completedHeroValue}>{selectedChampion?.name || 'TBD'}</span>
                      </div>
                      <div style={styles.completedHeroItem}>
                        <span style={styles.completedHeroLabel}>Prize Release</span>
                        <span style={styles.completedHeroValue}>{releaseDone ? 'Released' : 'Pending'}</span>
                      </div>
                      <div style={styles.completedHeroItem}>
                        <span style={styles.completedHeroLabel}>Your Finish</span>
                        <span style={styles.completedHeroValue}>
                          {selectedTournament?.myEntry?.finalRank ? `#${selectedTournament.myEntry.finalRank}` : '-'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ ...styles.ctaCard, ...(isMobile ? styles.ctaCardMobile : {}) }}>
                  <div style={styles.ctaMini}>Tournament Entry</div>
                  <div style={styles.ctaPrice}>{formatEntryCostBreakdown(selectedTournament)}</div>
                  <div style={styles.ctaSub}>
                    {String(selectedTournament.status || '').toLowerCase() === 'completed'
                      ? 'This tournament is finished.'
                      : isFull && !isRegistered
                        ? 'Registration closed. Tournament is full.'
                      : 'Entry fee + platform fee included'}
                  </div>

                  <button
                    type="button"
                    onClick={canOpenTournament ? () => onOpenTournamentLive?.(selectedTournament.id) : () => requestJoin(selectedTournament.id, selectedTournament)}
                    disabled={!canJoin && !canOpenTournament}
                    style={{
                      ...styles.joinBtn,
                      ...((!canJoin && !canOpenTournament) ? styles.joinBtnDisabled : {}),
                    }}
                  >
                    {actionLabel}
                  </button>
                  {hasTournamentGamesAccess(selectedTournament) ? (
                    <button
                      type="button"
                      onClick={() => handleOpenGames(selectedTournament.id, selectedTournament)}
                      disabled={gamesLoading || !selectedTournament}
                      style={{ ...styles.secondaryBtn, ...styles.gamesBtn, ...((gamesLoading || !selectedTournament) ? styles.secondaryBtnDisabled : {}) }}
                    >
                      Games
                    </button>
                  ) : null}

                  {selectedTournament?.myEntry?.finalRank ? (
                    <div style={styles.ctaInfo}>You finished at rank #{selectedTournament.myEntry.finalRank}.</div>
                  ) : null}
                  {shouldShowJoinedWaitingNote ? (
                    <div style={styles.ctaInfo}>You will enter tournament when TavlaPro starts round 1.</div>
                  ) : null}
                  {selectedChampion ? (
                    <div style={styles.ctaInfo}>Winner: {selectedChampion.name}</div>
                  ) : null}
                </div>
              </div>

              <div style={{ ...styles.contentCard, ...(isMobile ? styles.contentCardMobile : {}) }}>
                <SectionHeader
                  title={String(selectedTournament.status || '').toLowerCase() === 'completed' ? 'Completed Standings' : 'Current Leaders'}
                  subtitle="Winner name and standings stay visible for completed tournaments."
                />
                {detailLoading ? (
                  <div style={styles.detailLoaderCard}>Loading leaderboard...</div>
                ) : selectedTopRanks.length > 0 ? (
                  <div style={styles.rankBoard}>
                    {selectedTopRanks.map((participant) => (
                      <div key={participant.id} style={{ ...styles.rankRow, ...(isMobile ? styles.rankRowMobile : {}) }}>
                        <div style={{ ...styles.rankCircle, ...(isMobile ? styles.rankCircleMobile : {}), ...(Number(participant.finalRank || 0) <= 3 ? styles.rankCircleTop : {}) }}>
                          #{participant.finalRank || '-'}
                        </div>
                        <div style={styles.rankContent}>
                          <div style={{ ...styles.rankName, ...(isMobile ? styles.rankNameMobile : {}) }}>
                            <span>{participant.name}</span>
                            {String(participant.userId || '') === String(user?.id || '') ? <span style={styles.youTag}>You</span> : null}
                          </div>
                          <div style={{ ...styles.rankMeta, ...(isMobile ? styles.rankMetaMobile : {}) }}>
                            @{participant.username} • {participant.finalRankLabel || participant.status}
                          </div>
                        </div>
                        <div style={{ ...styles.rankReward, ...(isMobile ? styles.rankRewardMobile : {}) }}>
                          {participant.prizeCoins ? `${formatCoins(participant.prizeCoins)} coins` : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyCardInner}>Rankings will appear once tournament results are assigned.</div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      )}

      {showGamesModal && (
        <div
          style={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowGamesModal(false);
            setGamesTournament(null);
          }}
        >
          <div
            style={{ ...styles.modalCard, ...(isMobile ? styles.modalCardMobile : {}), ...styles.gamesModalCard }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Tournament Games</div>
                <div style={styles.modalSubtitle}>{gamesTournament?.name || 'Tournament'}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowGamesModal(false);
                  setGamesTournament(null);
                }}
                style={styles.modalCloseBtn}
              >
                Close
              </button>
            </div>
            {gamesLoading ? (
              <div style={styles.detailLoaderCard}>Loading tournament games...</div>
            ) : selectedGameRounds.length > 0 ? (
              <div style={styles.gamesRoundList}>
                {selectedGameRounds.map((round, index) => {
                  const subRounds = getRelatedSubRounds(round, allGameRounds);
                  return (
                  <div key={round.id} style={{ ...styles.gamesRoundCard, ...getRoundCardTone(index) }}>
                    <div style={styles.gamesRoundHeader}>
                      <div>
                        <div style={styles.gamesRoundTitle}>{round.label || `Round ${round.roundNumber}`}</div>
                        <div style={styles.gamesRoundMeta}>
                          {round.matches.length} game{round.matches.length === 1 ? '' : 's'} | {round.status || 'scheduled'}
                        </div>
                      </div>
                      <div style={styles.gamesRoundBadge}>Round {round.roundNumber || '-'}</div>
                    </div>
                    <div style={styles.gamesMatchList}>
                      {round.matches.map((match) => {
                        const scoreline = `${Number(match.score1 || 0)} - ${Number(match.score2 || 0)}`;
                        const winnerName = match.winnerName || 'Pending';
                        return (
                          <div key={match.id} style={styles.gamesMatchRow}>
                            <div style={styles.gamesPlayerBlock}>
                              <div style={styles.gamesPlayers}>
                                <span style={styles.gamesPlayerName}>{match.player1Name || 'TBD'}</span>
                                <span style={styles.gamesVs}>vs</span>
                                <span style={styles.gamesPlayerName}>{match.player2Name || 'TBD'}</span>
                              </div>
                              <div style={styles.gamesMatchMeta}>Winner: {winnerName}</div>
                            </div>
                            <div style={styles.gamesInfoBlock}>
                              <div style={styles.gamesScore}>{scoreline}</div>
                              <div style={styles.gamesMatchMeta}>{getMatchDisplayTime(match)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {subRounds.length > 0 ? (
                      <div style={styles.subRoundsWrap}>
                        <div style={styles.subRoundsTitle}>Sub Matches</div>
                        <div style={styles.subRoundsList}>
                          {subRounds.map((subRound) => (
                            <div key={subRound.id} style={styles.subRoundCard}>
                              <div style={styles.subRoundHeader}>
                                <div>
                                  <div style={styles.subRoundTitle}>{subRound.label || `Sub Match ${subRound.roundNumber || '-'}`}</div>
                                  <div style={styles.subRoundMeta}>
                                    {subRound.matches.length} game{subRound.matches.length === 1 ? '' : 's'} | {subRound.status || 'scheduled'}
                                  </div>
                                </div>
                                <div style={styles.subRoundBadge}>{subRound.rankRange || 'Placement'}</div>
                              </div>
                              <div style={styles.gamesMatchList}>
                                {subRound.matches.map((match) => {
                                  const scoreline = `${Number(match.score1 || 0)} - ${Number(match.score2 || 0)}`;
                                  const winnerName = match.winnerName || 'Pending';
                                  return (
                                    <div key={match.id} style={styles.subRoundMatchRow}>
                                      <div style={styles.gamesPlayerBlock}>
                                        <div style={styles.gamesPlayers}>
                                          <span style={styles.gamesPlayerName}>{match.player1Name || 'TBD'}</span>
                                          <span style={styles.gamesVs}>vs</span>
                                          <span style={styles.gamesPlayerName}>{match.player2Name || 'TBD'}</span>
                                        </div>
                                        <div style={styles.gamesMatchMeta}>Winner: {winnerName}</div>
                                      </div>
                                      <div style={styles.gamesInfoBlock}>
                                        <div style={styles.gamesScore}>{scoreline}</div>
                                        <div style={styles.gamesMatchMeta}>{getMatchDisplayTime(match)}</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.emptyCardInner}>Games will appear here once tournament rounds are created.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    display: 'grid',
    gap: 14,
    minHeight: 0,
    alignContent: 'start',
    background:
      'radial-gradient(circle at top left, rgba(214,166,102,0.22), transparent 28%), radial-gradient(circle at top right, rgba(255,214,165,0.18), transparent 22%), linear-gradient(180deg, #2a1a11 0%, #24160f 52%, #170c07 100%)',
  },
  pageMobile: {
    background:
      'radial-gradient(circle at 18% 0%, rgba(243,197,111,0.28), transparent 44%), radial-gradient(circle at 92% 12%, rgba(120,170,255,0.16), transparent 46%), radial-gradient(circle at 65% 52%, rgba(255,214,165,0.10), transparent 52%), linear-gradient(180deg, #352016 0%, #23150e 55%, #120906 100%)',
  },
  heroShell: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 18,
    border: '1px solid rgba(255,214,155,0.18)',
    background: 'linear-gradient(135deg, rgba(92,58,32,0.95), rgba(46,28,17,0.98))',
    boxShadow: '0 14px 30px rgba(0,0,0,0.26)',
  },
  heroShellMobile: {
    borderRadius: 16,
    border: '1px solid rgba(215,177,106,0.2)',
    background: 'linear-gradient(180deg, rgba(64,38,22,0.98), rgba(24,14,9,0.99))',
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
  },
  heroGlowOne: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'rgba(255,196,110,0.11)',
    filter: 'blur(30px)',
    top: -80,
    right: -40,
    pointerEvents: 'none',
  },
  heroGlowTwo: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: '50%',
    background: 'rgba(120,170,255,0.07)',
    filter: 'blur(35px)',
    bottom: -60,
    left: -40,
    pointerEvents: 'none',
  },
  heroTopRow: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 8,
    alignItems: 'flex-start',
  },
  heroTopRowMobile: {
    display: 'block',
    gap: 0,
    alignItems: 'start',
  },
  heroMain: {
    display: 'grid',
    gap: 6,
    alignContent: 'start',
    minWidth: 0,
    flex: '1 1 420px',
  },
  heroMainMobile: {
    gap: 2,
    width: '100%',
  },
  heroActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  backButton: {
    border: '1px solid rgba(255,224,178,0.28)',
    borderRadius: 999,
    background: 'rgba(24,14,10,0.72)',
    color: '#fff0cb',
    padding: '6px 12px',
    fontWeight: 800,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  backButtonMobile: {
    padding: '4px 10px',
    fontSize: 11,
    minHeight: 30,
    marginBottom: 2,
  },
  kicker: {
    color: '#f3c56f',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    fontWeight: 900,
    fontSize: 11,
  },
  title: {
    color: '#ffffff',
    lineHeight: 1.12,
    fontWeight: 900,
    maxWidth: 720,
  },
  titleMobile: {
    lineHeight: 1.08,
    maxWidth: '100%',
    fontWeight: 800,
  },
  meta: {
    color: 'rgba(255,255,255,0.74)',
    lineHeight: 1.45,
    maxWidth: 720,
    fontSize: 12,
  },
  heroPills: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  heroPill: {
    padding: '6px 9px',
    borderRadius: 999,
    background: 'rgba(255,246,233,0.10)',
    color: '#fff1cf',
    border: '1px solid rgba(255,228,185,0.2)',
    fontWeight: 700,
    fontSize: 10,
  },
  payoutStrip: {
    marginTop: 10,
    borderRadius: 16,
    padding: '10px 12px',
    border: '1px solid rgba(255,214,155,0.14)',
    background:
      'radial-gradient(circle at 16% 0%, rgba(243,197,111,0.18), transparent 52%), radial-gradient(circle at 92% 20%, rgba(120,170,255,0.10), transparent 46%), linear-gradient(180deg, rgba(16,10,7,0.36), rgba(10,6,4,0.16))',
    display: 'grid',
    gap: 8,
    boxShadow: '0 14px 34px rgba(0,0,0,0.22)',
  },
  payoutStripMobile: {
    borderRadius: 14,
    padding: '8px 10px',
    gap: 6,
  },
  payoutTitle: {
    color: 'rgba(244,220,182,0.90)',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  payoutSubtitle: {
    color: 'rgba(255,255,255,0.70)',
    fontSize: 12,
    fontWeight: 800,
    marginTop: 4,
    lineHeight: 1.25,
  },
  payoutTopRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
  },
  payoutTopRowMobile: {
    gridTemplateColumns: '1fr',
    gap: 8,
  },
  payoutCardLarge: {
    borderRadius: 16,
    padding: '10px 12px',
    border: '1px solid rgba(255,214,155,0.16)',
    background: 'rgba(0,0,0,0.14)',
    boxShadow: '0 12px 26px rgba(0,0,0,0.16)',
    display: 'grid',
    gap: 0,
    minWidth: 0,
  },
  payoutRow: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  payoutRankLarge: {
    color: 'rgba(255,244,226,0.92)',
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
  },
  payoutLabelInline: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 12,
    fontWeight: 950,
    lineHeight: 1.1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  payoutPctLarge: {
    color: '#fff8ee',
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  payoutGridRest: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 6,
  },
  payoutGridRestMobile: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  },
  payoutCardSmall: {
    borderRadius: 14,
    padding: '9px 10px',
    border: '1px solid rgba(255,214,155,0.12)',
    background: 'rgba(0,0,0,0.12)',
    boxShadow: '0 10px 22px rgba(0,0,0,0.12)',
    display: 'grid',
    gap: 0,
    minWidth: 0,
  },
  payoutRankSmall: {
    color: 'rgba(255,244,226,0.84)',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.05,
  },
  payoutPctSmall: {
    color: 'rgba(255,231,189,0.92)',
    fontSize: 12,
    fontWeight: 950,
    lineHeight: 1.05,
    textAlign: 'right',
  },
  balanceStrip: {
    borderRadius: 16,
    padding: '10px 12px',
    border: '1px solid rgba(255,218,154,0.16)',
    background: 'linear-gradient(180deg, rgba(132,86,45,0.76), rgba(75,46,24,0.82))',
    display: 'grid',
    gap: 5,
    minWidth: 200,
  },
  balanceStripMobile: {
    minWidth: 0,
    width: '100%',
    padding: '7px 10px',
    borderRadius: 12,
    justifySelf: 'stretch',
    alignSelf: 'start',
  },
  balanceTopLine: {
    display: 'grid',
    gap: 2,
  },
  balanceLabel: {
    color: '#d3b88b',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: 800,
  },
  balanceLabelMobile: {
    fontSize: 10,
    letterSpacing: 1,
  },
  balanceValue: {
    color: '#fff0cb',
    fontSize: 18,
    fontWeight: 900,
  },
  balanceValueMobile: {
    fontSize: 16,
    lineHeight: 1.1,
  },
  balanceMeta: {
    color: '#e2cda7',
    fontSize: 12,
    lineHeight: 1.4,
  },
  notice: {
    borderRadius: 14,
    padding: '13px 16px',
    fontWeight: 800,
    border: '1px solid transparent',
  },
  noticeOk: {
    background: 'rgba(61,120,77,0.22)',
    color: '#d7ffdc',
    borderColor: 'rgba(127,222,150,0.16)',
  },
  noticeError: {
    background: 'rgba(145,55,55,0.22)',
    color: '#ffe0e0',
    borderColor: 'rgba(255,145,145,0.16)',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(6px)',
    display: 'grid',
    placeItems: 'center',
    padding: 14,
    zIndex: 60,
  },
  modalCard: {
    width: 'min(720px, 96vw)',
    maxHeight: 'min(78vh, 820px)',
    overflow: 'auto',
    borderRadius: 18,
    border: '1px solid rgba(255,214,155,0.18)',
    background: 'linear-gradient(180deg, rgba(52,32,19,0.98), rgba(22,14,10,0.98))',
    boxShadow: '0 26px 70px rgba(0,0,0,0.45)',
    padding: 14,
  },
  modalCardMobile: {
    width: 'min(680px, 96vw)',
    maxHeight: 'min(82vh, 900px)',
    borderRadius: 16,
    padding: 12,
  },
  gamesModalCard: {
    width: 'min(920px, 96vw)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingBottom: 10,
    borderBottom: '1px solid rgba(255,214,155,0.14)',
    marginBottom: 12,
  },
  modalTitle: {
    color: '#fff4df',
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1.1,
  },
  modalSubtitle: {
    color: 'rgba(244,220,182,0.86)',
    fontSize: 12,
    fontWeight: 800,
    marginTop: 6,
  },
  modalCloseBtn: {
    borderRadius: 12,
    padding: '10px 12px',
    border: '1px solid rgba(255,214,155,0.18)',
    background: 'rgba(10,6,4,0.35)',
    color: '#fff1d7',
    fontWeight: 900,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  toastBar: {
    position: 'fixed',
    top: 14,
    right: 14,
    zIndex: 90,
    width: 'min(520px, calc(100vw - 28px))',
    borderRadius: 14,
    border: '1px solid rgba(255,214,155,0.18)',
    boxShadow: '0 18px 44px rgba(0,0,0,0.36)',
    padding: '10px 12px',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 10,
    alignItems: 'start',
    backdropFilter: 'blur(10px)',
  },
  toastBarOk: {
    background: 'linear-gradient(180deg, rgba(36,74,46,0.92), rgba(18,34,24,0.95))',
    borderColor: 'rgba(127,222,150,0.22)',
    color: '#dbffe3',
  },
  toastBarError: {
    background: 'linear-gradient(180deg, rgba(96,30,30,0.92), rgba(38,16,16,0.96))',
    borderColor: 'rgba(255,145,145,0.22)',
    color: '#ffe6e6',
  },
  toastText: {
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  toastCloseBtn: {
    borderRadius: 12,
    padding: '6px 10px',
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(0,0,0,0.22)',
    color: 'inherit',
    fontWeight: 950,
    cursor: 'pointer',
    lineHeight: 1,
  },
  confirmCard: {
    width: 'min(520px, 96vw)',
    maxHeight: 'min(72vh, 700px)',
  },
  confirmBody: {
    display: 'grid',
    gap: 10,
    padding: '2px 2px 10px',
  },
  confirmLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '10px 12px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,214,145,0.10)',
  },
  confirmKey: {
    color: 'rgba(244,220,182,0.86)',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  confirmValue: {
    color: '#fff4df',
    fontSize: 13,
    fontWeight: 950,
  },
  confirmHint: {
    color: 'rgba(244,220,182,0.76)',
    fontSize: 12,
    lineHeight: 1.35,
    paddingLeft: 4,
  },
  confirmFooter: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 4,
  },
  confirmCancelBtn: {
    background: 'rgba(10,6,4,0.35)',
    border: '1px solid rgba(255,214,155,0.18)',
    boxShadow: 'none',
  },
  confirmJoinBtn: {
    boxShadow: '0 10px 18px rgba(49,92,35,0.22)',
  },
  layout: {
    display: 'grid',
    gap: 14,
    alignItems: 'start',
  },
  showcaseLayout: {
    display: 'grid',
    gap: 18,
    alignItems: 'start',
    width: '100%',
    maxWidth: 1160,
    margin: '0 auto',
  },
  showcaseLayoutMobile: {
    gap: 12,
    maxWidth: '100%',
  },
  showcaseFeatured: {
    display: 'grid',
    gap: 12,
  },
  showcaseFeaturedMobile: {
    gap: 10,
  },
  showcaseFeaturedCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    border: '1px solid rgba(255,214,155,0.18)',
    background: 'linear-gradient(135deg, rgba(98,60,33,0.94), rgba(44,26,16,0.98))',
    boxShadow: '0 14px 30px rgba(0,0,0,0.26)',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 280px',
    gap: 14,
    padding: 16,
  },
  showcaseFeaturedCardMobile: {
    borderRadius: 18,
    gridTemplateColumns: '1fr',
    padding: 12,
    gap: 12,
  },
  trophyArt: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage:
      "radial-gradient(circle at 78% 40%, rgba(255,198,120,0.20), transparent 54%), radial-gradient(circle at 72% 52%, rgba(255,176,88,0.14), transparent 62%), linear-gradient(90deg, rgba(0,0,0,0.0), rgba(0,0,0,0.18) 62%, rgba(0,0,0,0.38) 100%)",
    opacity: 0.95,
  },
  showcaseFeaturedContent: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gap: 10,
    minWidth: 0,
  },
  showcaseFeaturedTop: {
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  showcaseFeaturedLabel: {
    color: 'rgba(255,232,204,0.82)',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  showcaseFeaturedTitle: {
    color: '#fff4df',
    fontWeight: 950,
    lineHeight: 1.1,
    marginTop: 4,
    textShadow: '0 8px 14px rgba(0,0,0,0.28)',
  },
  showcaseFeaturedMeta: {
    color: 'rgba(233,205,160,0.88)',
    fontSize: 12,
    marginTop: 6,
    lineHeight: 1.4,
  },
  showcaseFeaturedChips: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  showcaseFeaturedChipsMobile: {
    gap: 8,
  },
  featureChip: {
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(18,11,7,0.42)',
    border: '1px solid rgba(255,214,155,0.20)',
    color: 'rgba(255,241,221,0.92)',
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  showcaseFeaturedStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },
  showcaseFeaturedStatsMobile: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  featuredStat: {
    borderRadius: 14,
    border: '1px solid rgba(255,214,155,0.14)',
    background: 'rgba(16,10,7,0.36)',
    padding: '9px 10px',
    minWidth: 0,
  },
  featuredStatLabel: {
    color: 'rgba(244,220,182,0.86)',
    fontSize: 12,
    fontWeight: 800,
  },
  featuredStatValue: {
    color: '#fff6e6',
    fontSize: 14,
    fontWeight: 950,
    marginTop: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showcaseBrowse: {
    display: 'grid',
    gap: 12,
  },
  browseHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 14,
    flexWrap: 'wrap',
  },
  browseHeaderMobile: {
    alignItems: 'stretch',
  },
  filterTabs: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterTab: {
    borderRadius: 999,
    padding: '9px 12px',
    border: '1px solid rgba(255,214,155,0.14)',
    background: 'rgba(12,8,6,0.42)',
    color: 'rgba(255,232,204,0.82)',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
  },
  filterTabActive: {
    background: 'linear-gradient(180deg, rgba(120,74,44,0.72), rgba(52,30,18,0.84))',
    borderColor: 'rgba(255,214,155,0.26)',
    color: '#fff4df',
  },
  cardGrid: {
    display: 'grid',
    gap: 14,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  },
  cardGridMobile: {
    gridTemplateColumns: '1fr',
    gap: 10,
  },
  tournamentCard: {
    borderRadius: 18,
    border: '1px solid rgba(255,214,155,0.14)',
    background: 'linear-gradient(180deg, rgba(58,34,21,0.95), rgba(30,18,12,0.985))',
    boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
    padding: 16,
    display: 'grid',
    gap: 12,
    cursor: 'pointer',
    outline: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
  },
  tournamentCardActive: {
    borderColor: 'rgba(255,214,155,0.30)',
    boxShadow: '0 14px 32px rgba(0,0,0,0.22)',
  },
  tournamentCardMobile: {
    padding: 14,
    gap: 10,
  },
  tournamentCardTop: {
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  tournamentCardTitle: {
    color: '#fff0cb',
    fontWeight: 950,
    fontSize: 17,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tournamentCardMeta: {
    color: 'rgba(233,205,160,0.86)',
    fontSize: 12,
    marginTop: 5,
    lineHeight: 1.2,
  },
  tournamentCardBottom: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'start',
    gap: 10,
  },
  tournamentCardBottomMobile: {
    gridTemplateColumns: '1fr',
    gap: 12,
  },
  coinRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  coinStack: {
    display: 'grid',
    gap: 2,
    minWidth: 0,
  },
  coinDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'linear-gradient(180deg, #f5d27b, #b77b32)',
    boxShadow: '0 0 0 2px rgba(255,214,155,0.12)',
    flex: '0 0 auto',
  },
  coinValue: {
    color: 'rgba(255,232,204,0.92)',
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1.25,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  coinSubValue: {
    color: 'rgba(233,205,160,0.86)',
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.25,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  cardCtaBtn: {
    borderRadius: 12,
    padding: '10px 12px',
    width: '100%',
    minWidth: 0,
    border: '1px solid rgba(122,196,141,0.22)',
    background: 'linear-gradient(180deg, rgba(88,168,106,0.92), rgba(46,112,64,0.92))',
    color: '#f0fff3',
    fontWeight: 950,
    fontSize: 12,
    cursor: 'pointer',
    boxShadow: '0 10px 18px rgba(0,0,0,0.22)',
  },
  cardCtaBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    filter: 'grayscale(0.35)',
  },
  cardActions: {
    display: 'grid',
    gap: 8,
    justifyItems: 'stretch',
    flex: '0 0 auto',
    width: 130,
  },
  cardActionsMobile: {
    width: '100%',
  },
  cardSecondaryBtn: {
    borderRadius: 12,
    padding: '10px 12px',
    width: '100%',
    minWidth: 0,
    color: '#fff1d7',
    fontWeight: 900,
    fontSize: 12,
    cursor: 'pointer',
  },
  cardLeaderboardBtn: {
    border: '1px solid rgba(167,199,255,0.26)',
    background: 'linear-gradient(180deg, rgba(70,104,171,0.92), rgba(37,60,109,0.94))',
    boxShadow: '0 10px 18px rgba(25,46,86,0.18)',
  },
  cardGamesBtn: {
    border: '1px solid rgba(255,211,148,0.26)',
    background: 'linear-gradient(180deg, rgba(174,106,45,0.94), rgba(111,62,22,0.96))',
    boxShadow: '0 10px 18px rgba(76,43,14,0.18)',
  },
  cardSecondaryBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  sidebar: {
    display: 'grid',
    gap: 10,
    borderRadius: 18,
    border: '1px solid rgba(255,220,178,0.14)',
    background: 'linear-gradient(180deg, rgba(39,23,14,0.96), rgba(25,15,10,0.98))',
    boxShadow: '0 12px 28px rgba(0,0,0,0.22)',
  },
  mainPanel: {
    display: 'grid',
    gap: 18,
    minWidth: 0,
  },
  sectionHeader: {
    display: 'grid',
    gap: 4,
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: 900,
  },
  sectionSubtitle: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  list: {
    display: 'grid',
    gap: 10,
    overflowY: 'auto',
    paddingRight: 2,
  },
  listMobile: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(220px, 86vw)',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 4,
    maxWidth: '100%',
  },
  listItem: {
    border: '1px solid rgba(255,221,176,0.16)',
    background: 'linear-gradient(180deg, rgba(40,24,16,0.98), rgba(24,14,10,0.99))',
    color: '#fff8ee',
    borderRadius: 16,
    padding: 12,
    textAlign: 'left',
    display: 'grid',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
  },
  listItemMobile: {
    minHeight: 126,
    alignContent: 'start',
  },
  listItemActive: {
    borderColor: 'rgba(255,229,188,0.42)',
    background: 'linear-gradient(135deg, rgba(96,58,30,0.98), rgba(46,27,17,1))',
    transform: 'translateY(-1px)',
    boxShadow: '0 14px 28px rgba(0,0,0,0.28)',
  },
  listItemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    alignItems: 'flex-start',
  },
  listTitleBlock: {
    display: 'grid',
    gap: 4,
    minWidth: 0,
  },
  listTitle: {
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.25,
    wordBreak: 'break-word',
    color: '#fff8f0',
  },
  listTitleMobile: {
    fontSize: 14,
    lineHeight: 1.2,
  },
  listMeta: {
    color: 'rgba(255,244,226,0.78)',
    fontSize: 12,
  },
  listFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    alignItems: 'center',
    color: '#fff0d4',
    fontSize: 12,
    fontWeight: 700,
    flexWrap: 'wrap',
  },
  listFooterMobile: {
    fontSize: 11,
    gap: 6,
  },
  completedListMeta: {
    display: 'grid',
    gap: 6,
    paddingTop: 8,
    borderTop: '1px solid rgba(255,214,145,0.16)',
  },
  completedListLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  completedListKey: {
    color: 'rgba(255,229,190,0.72)',
    fontSize: 12,
    fontWeight: 700,
  },
  completedListValue: {
    color: '#fff8ee',
    fontSize: 12,
    fontWeight: 800,
  },
  statusBadge: {
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'capitalize',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  },
  statusBadgeLarge: {
    borderRadius: 999,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'capitalize',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
    height: 'fit-content',
  },
  badgeSuccess: {
    background: 'rgba(74,167,98,0.18)',
    color: '#ccffd6',
    borderColor: 'rgba(97,228,130,0.16)',
  },
  badgeWarning: {
    background: 'rgba(209,144,44,0.18)',
    color: '#ffe1a6',
    borderColor: 'rgba(255,191,95,0.16)',
  },
  badgeMuted: {
    background: 'rgba(120,120,130,0.16)',
    color: '#e2e2ea',
    borderColor: 'rgba(210,210,220,0.12)',
  },
  badgeDefault: {
    background: 'rgba(96,113,150,0.16)',
    color: '#d9e5ff',
    borderColor: 'rgba(168,192,255,0.12)',
  },
  modeBadge: {
    borderRadius: 999,
    padding: '5px 10px',
    background: 'rgba(255,255,255,0.06)',
    color: '#ffd794',
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'capitalize',
  },
  progressTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.07)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #f1b35f, #f7d28b)',
  },
  topBanner: {
    display: 'grid',
    gap: 12,
    borderRadius: 18,
    border: '1px solid rgba(255,220,178,0.14)',
    background: 'linear-gradient(135deg, rgba(58,35,22,0.98), rgba(31,19,12,0.99))',
    boxShadow: '0 14px 30px rgba(0,0,0,0.22)',
  },
  topBannerMobile: {
    gap: 8,
  },
  topBannerLeft: {
    display: 'grid',
    gap: 12,
    minWidth: 0,
  },
  topBannerHead: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 14,
    alignItems: 'flex-start',
  },
  detailTitle: {
    color: '#fff0cb',
    fontWeight: 900,
    lineHeight: 1.2,
  },
  detailMeta: {
    color: '#d4bb92',
    marginTop: 6,
    lineHeight: 1.7,
    fontSize: 13,
  },
  quickStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 8,
  },
  quickStatsMobile: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  quickStat: {
    minWidth: 0,
    padding: '7px 9px',
    borderRadius: 12,
    background: 'rgba(255,246,233,0.06)',
    border: '1px solid rgba(255,229,188,0.12)',
    display: 'grid',
    gap: 2,
  },
  quickStatLabel: {
    color: '#cbb088',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
    fontWeight: 800,
  },
  quickStatValue: {
    color: '#fff0cb',
    fontWeight: 900,
    fontSize: 13,
    wordBreak: 'break-word',
  },
  progressBlock: {
    display: 'grid',
    gap: 8,
  },
  progressHead: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  progressTitle: {
    color: '#f2d6a0',
    fontSize: 13,
    fontWeight: 800,
  },
  progressValue: {
    color: '#ffe8bd',
    fontSize: 12,
    fontWeight: 800,
  },
  progressTrackLarge: {
    width: '100%',
    height: 12,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  progressFillLarge: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #e9ae56, #f8d693)',
    boxShadow: '0 0 12px rgba(248,214,147,0.22)',
  },
  completedHero: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
  },
  completedHeroItem: {
    padding: '12px 13px',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,214,145,0.10)',
    display: 'grid',
    gap: 5,
  },
  completedHeroLabel: {
    color: '#cbb088',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: 800,
  },
  completedHeroValue: {
    color: '#fff0cb',
    fontSize: 16,
    fontWeight: 900,
    wordBreak: 'break-word',
  },
  ctaCard: {
    position: 'relative',
    zIndex: 1,
    borderRadius: 16,
    padding: 10,
    border: '1px solid rgba(255,223,182,0.16)',
    background: 'linear-gradient(180deg, rgba(36,22,14,0.98), rgba(20,12,9,0.99))',
    display: 'grid',
    gap: 6,
    alignContent: 'start',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
  },
  ctaCardMobile: {
    padding: 9,
  },
  ctaMini: {
    color: '#dcc29a',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
    fontWeight: 800,
  },
  ctaPrice: {
    color: '#fff8ee',
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1,
  },
  ctaSub: {
    color: 'rgba(255,244,226,0.78)',
    fontSize: 12,
    lineHeight: 1.45,
  },
  ctaInfo: {
    color: '#fff0d4',
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  joinBtn: {
    width: '100%',
    border: '1px solid rgba(255,226,170,0.20)',
    background: 'linear-gradient(135deg, #4a7c38, #355d27)',
    color: '#fff7e2',
    padding: '9px 12px',
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 900,
    fontSize: 12,
    boxShadow: '0 8px 18px rgba(49,92,35,0.22)',
  },
  joinBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  secondaryBtn: {
    width: '100%',
    border: '1px solid rgba(255,226,170,0.22)',
    color: '#fff6e6',
    padding: '9px 12px',
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 900,
    fontSize: 12,
    boxShadow: '0 10px 18px rgba(0,0,0,0.18)',
  },
  leaderboardBtn: {
    background: 'linear-gradient(180deg, rgba(70,104,171,0.92), rgba(37,60,109,0.94))',
    border: '1px solid rgba(167,199,255,0.26)',
    boxShadow: '0 10px 18px rgba(25,46,86,0.22)',
  },
  gamesBtn: {
    background: 'linear-gradient(180deg, rgba(174,106,45,0.94), rgba(111,62,22,0.96))',
    border: '1px solid rgba(255,211,148,0.26)',
    boxShadow: '0 10px 18px rgba(76,43,14,0.22)',
  },
  secondaryBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  gamesRoundList: {
    display: 'grid',
    gap: 12,
  },
  gamesRoundCard: {
    display: 'grid',
    gap: 10,
    borderRadius: 18,
    border: '1px solid rgba(255,214,155,0.14)',
    padding: 14,
    position: 'relative',
    overflow: 'hidden',
  },
  gamesRoundHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    paddingBottom: 8,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  gamesRoundTitle: {
    color: '#fff4df',
    fontSize: 17,
    fontWeight: 900,
    lineHeight: 1.15,
  },
  gamesRoundMeta: {
    color: 'rgba(244,220,182,0.82)',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 4,
  },
  gamesRoundBadge: {
    borderRadius: 999,
    padding: '6px 10px',
    background: 'rgba(255,214,155,0.12)',
    border: '1px solid rgba(255,214,155,0.16)',
    color: '#fff1d7',
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  gamesMatchList: {
    display: 'grid',
    gap: 8,
  },
  gamesMatchRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 12,
    alignItems: 'center',
    borderRadius: 14,
    padding: '12px 14px',
    background: 'linear-gradient(180deg, rgba(8,6,5,0.24), rgba(8,6,5,0.42))',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  gamesPlayerBlock: {
    display: 'grid',
    gap: 4,
    minWidth: 0,
  },
  gamesPlayers: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  gamesPlayerName: {
    color: '#fff8ee',
    fontSize: 14,
    fontWeight: 900,
    wordBreak: 'break-word',
  },
  gamesVs: {
    color: 'rgba(244,220,182,0.72)',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  gamesInfoBlock: {
    display: 'grid',
    gap: 4,
    justifyItems: 'end',
    textAlign: 'right',
  },
  gamesScore: {
    color: '#ffd894',
    fontSize: 15,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  gamesMatchMeta: {
    color: 'rgba(244,220,182,0.82)',
    fontSize: 12,
    lineHeight: 1.4,
  },
  subRoundsWrap: {
    display: 'grid',
    gap: 10,
    marginTop: 4,
    paddingTop: 12,
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  subRoundsTitle: {
    color: '#ffe0a8',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  subRoundsList: {
    display: 'grid',
    gap: 10,
  },
  subRoundCard: {
    display: 'grid',
    gap: 10,
    borderRadius: 16,
    padding: 12,
    background: 'linear-gradient(180deg, rgba(12,10,14,0.22), rgba(12,10,14,0.42))',
    border: '1px solid rgba(184,205,255,0.12)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  subRoundHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
    flexWrap: 'wrap',
  },
  subRoundTitle: {
    color: '#eef4ff',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  subRoundMeta: {
    color: 'rgba(220,230,255,0.76)',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 1.4,
  },
  subRoundBadge: {
    borderRadius: 999,
    padding: '6px 10px',
    background: 'rgba(142,181,255,0.12)',
    border: '1px solid rgba(142,181,255,0.18)',
    color: '#e5eeff',
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  subRoundMatchRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 12,
    alignItems: 'center',
    borderRadius: 12,
    padding: '10px 12px',
    background: 'linear-gradient(180deg, rgba(6,8,12,0.16), rgba(6,8,12,0.32))',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  infoGrid: {
    display: 'grid',
    gap: 12,
  },
  infoCard: {
    border: '1px solid rgba(255,214,145,0.12)',
    borderRadius: 18,
    background: 'linear-gradient(180deg, rgba(45,28,17,0.92), rgba(27,16,11,0.96))',
    padding: 16,
    boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
  },
  infoLabel: {
    color: '#d5b98e',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: 800,
  },
  infoValue: {
    color: '#fff0cb',
    marginTop: 8,
    lineHeight: 1.5,
    fontWeight: 900,
    fontSize: 16,
    wordBreak: 'break-word',
  },
  infoHint: {
    color: '#c9b08a',
    marginTop: 8,
    fontSize: 12,
    lineHeight: 1.45,
  },
  contentCard: {
    display: 'grid',
    gap: 8,
    borderRadius: 18,
    padding: 12,
    border: '1px solid rgba(255,223,182,0.14)',
    background: 'linear-gradient(180deg, rgba(39,23,14,0.96), rgba(25,15,10,0.98))',
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
  },
  contentCardMobile: {
    padding: 10,
  },
  rankBoard: {
    display: 'grid',
    gap: 8,
  },
  rankRow: {
    display: 'grid',
    gridTemplateColumns: '38px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    background: 'linear-gradient(180deg, rgba(92,58,34,0.92), rgba(63,39,24,0.95))',
    padding: '8px 10px',
    border: '1px solid rgba(255,223,182,0.12)',
  },
  rankRowMobile: {
    gridTemplateColumns: '32px minmax(0, 1fr) auto',
    gap: 6,
    padding: '7px 8px',
  },
  rankCircle: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: '#744820',
    color: '#fff0cb',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 900,
    flexShrink: 0,
    fontSize: 12,
  },
  rankCircleMobile: {
    width: 26,
    height: 26,
    fontSize: 11,
  },
  rankCircleTop: {
    background: 'linear-gradient(135deg, #c08a3e, #8b5a25)',
  },
  rankContent: {
    minWidth: 0,
  },
  rankName: {
    color: '#fff0cb',
    fontWeight: 900,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 14,
  },
  rankNameMobile: {
    fontSize: 12,
  },
  youTag: {
    borderRadius: 999,
    padding: '2px 6px',
    background: 'rgba(83,137,255,0.14)',
    color: '#dce8ff',
    fontSize: 10,
    fontWeight: 900,
  },
  rankMeta: {
    color: '#d0b58c',
    fontSize: 11,
    marginTop: 2,
    wordBreak: 'break-word',
  },
  rankMetaMobile: {
    fontSize: 10,
    marginTop: 1,
  },
  rankReward: {
    color: '#d6ffc9',
    fontWeight: 900,
    textAlign: 'right',
    fontSize: 13,
  },
  rankRewardMobile: {
    fontSize: 11,
  },
  emptyCard: {
    borderRadius: 18,
    padding: 18,
    background: 'rgba(33,20,13,0.92)',
    border: '1px dashed rgba(255,214,145,0.14)',
    color: '#ffffff',
    fontWeight: 900,
  },
  emptyCardInner: {
    borderRadius: 16,
    padding: 16,
    background: 'rgba(33,20,13,0.82)',
    border: '1px dashed rgba(255,214,145,0.12)',
    color: '#ffffff',
    fontWeight: 900,
  },
  detailLoaderCard: {
    borderRadius: 16,
    padding: 16,
    background: 'rgba(33,20,13,0.82)',
    border: '1px solid rgba(255,214,145,0.18)',
    color: '#f6e7c1',
    fontWeight: 800,
  },
};
